import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChefHat, Camera, FileText, MessageSquare, BarChart3, Settings2, ClipboardList, MessageCircle } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { MonthPicker } from '@/components/MonthPicker';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/** v0.3.x (AIトークン利用上限管理、F-1): 使用状況ダッシュボード。
 *  設計: docs/design/v03-ai-token-quota.md §7.2 / detail §4.1 */

type Summary = {
  month: string;
  nodeId: string | null;
  totalCostUsd: number;
  totalTokens: number;
  requestCount: number;
  unassignedCostUsd: number;
  /** v0.4 ⑯ D-424/D-428: 「所属なし」を回数でも返す */
  unassignedRequestCount: number;
  previousMonthCostUsd: number;
  byFeature: Array<{ feature: string; cost_usd: number; tokens: number; request_count: number }>;
  byModel: Array<{ model: string | null; cost_usd: number; tokens: number }>;
  topUsers: Array<{ user_id: string; display_name: string; cost_usd: number; tokens: number; request_count: number }>;
  byDay: Array<{ date: string; cost_usd: number; tokens: number }>;
  activeLimit: { id: string; scope: string; costUsdLimit: number; tokenLimit: number | null; enforcementMode: string; utilizationPct: number } | null;
};

// v0.5⑧ 増分 経営会議ブリーフ対話AI (§8 co-change #4/#6): board_chat 追加 + 既存の欠落分
// (ai_setup/board_brief、ICONS はさらに analysis) を補完。
const FEATURE_LABELS: Record<string, string> = {
  menu_dev: 'AI メニュー開発',
  ocr: 'AI-OCR',
  auto_comment: 'AI 自動コメント',
  analysis: 'AI 分析',
  daily_report_ocr: '日報 OCR (写真添付)',
  ai_setup: 'AI セットアップ',
  board_brief: 'AI 経営会議ブリーフ',
  board_chat: 'AI 経営会議対話',
};
const FEATURE_ICONS: Record<string, typeof ChefHat> = {
  menu_dev: ChefHat,
  ocr: FileText,
  auto_comment: MessageSquare,
  daily_report_ocr: Camera,
  analysis: BarChart3,
  ai_setup: Settings2,
  board_brief: ClipboardList,
  board_chat: MessageCircle,
};

const currentYearMonth = (): string => {
  const now = new Date();
  const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return `${jst.getUTCFullYear()}-${String(jst.getUTCMonth() + 1).padStart(2, '0')}`;
};

export function AiUsageDashboardPanel() {
  const [month, setMonth] = useState(currentYearMonth());
  const [nodeId, setNodeId] = useState<string>('');

  const { data: nodesData } = useQuery<NodesResponse>({
    queryKey: ['nodes'],
    queryFn: () => apiFetch<NodesResponse>('/nodes'),
    staleTime: 5 * 60 * 1000,
  });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  const { data, isLoading } = useQuery<Summary>({
    queryKey: ['ai-usage-summary', month, nodeId],
    queryFn: () => apiFetch<Summary>(`/ai-usage?month=${month}${nodeId ? `&node_id=${nodeId}` : ''}`),
  });

  const momPct = data && data.previousMonthCostUsd > 0
    ? Math.round(((data.totalCostUsd - data.previousMonthCostUsd) / data.previousMonthCostUsd) * 1000) / 10
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label htmlFor="ai-usage-month" className="block text-xs text-text-sub mb-1">対象月</label>
          <MonthPicker id="ai-usage-month" aria-label="対象月" value={month} onChange={setMonth}
            className="px-3 py-2 rounded border border-surface-line focus-ring text-sm" />
        </div>
        <div className="min-w-[220px]">
          <span className="block text-xs text-text-sub mb-1" id="ai-usage-node-label">対象ノード</span>
          <Select value={nodeId || 'all'} onValueChange={(v) => setNodeId(v === 'all' ? '' : v)}>
            <SelectTrigger aria-labelledby="ai-usage-node-label">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全体 (テナント合計)</SelectItem>
              {flatNodes.map(({ node, depth }) => (
                <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading && <p className="text-sm text-text-sub">読み込み中…</p>}
      {!isLoading && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="border border-surface-line rounded-lg bg-surface-card p-4">
              <div className="text-xs text-text-sub mb-1">当月消費</div>
              <div className="text-2xl font-bold tabular-nums">${data.totalCostUsd.toFixed(2)}</div>
              {momPct !== null && (
                <div className={`text-xs mt-1 ${momPct >= 0 ? 'text-alert-text' : 'text-success-text'}`}>
                  前月比 {momPct >= 0 ? '+' : ''}{momPct}%
                </div>
              )}
            </div>
            {data.activeLimit ? (
              <div className="border border-surface-line rounded-lg bg-surface-card p-4">
                <div className="text-xs text-text-sub mb-1">上限進捗</div>
                <div className="text-2xl font-bold tabular-nums">{data.activeLimit.utilizationPct}%</div>
                <div className="w-full h-1.5 rounded-full bg-surface-sunken mt-2 overflow-hidden" role="progressbar"
                  aria-valuenow={data.activeLimit.utilizationPct} aria-valuemin={0} aria-valuemax={100}>
                  <div
                    className={`h-full ${data.activeLimit.utilizationPct >= 100 ? 'bg-danger-border' : data.activeLimit.utilizationPct >= 80 ? 'bg-alert-border' : 'bg-chrome-base'}`}
                    style={{ width: `${Math.min(100, data.activeLimit.utilizationPct)}%` }}
                  />
                </div>
                <div className="text-xs text-text-sub mt-1">上限 ${data.activeLimit.costUsdLimit.toFixed(2)}/月</div>
              </div>
            ) : (
              <div className="border border-surface-line rounded-lg bg-surface-card p-4">
                <div className="text-xs text-text-sub mb-1">上限進捗</div>
                <div className="text-sm text-text-sub mt-2">このスコープに上限は設定されていません</div>
              </div>
            )}
            <div className="border border-surface-line rounded-lg bg-surface-card p-4">
              <div className="text-xs text-text-sub mb-1">リクエスト数</div>
              <div className="text-2xl font-bold tabular-nums">{data.requestCount.toLocaleString()}</div>
            </div>
            <div className="border border-surface-line rounded-lg bg-surface-card p-4">
              <div className="text-xs text-text-sub mb-1">トークン数 (概算)</div>
              <div className="text-2xl font-bold tabular-nums">{data.totalTokens.toLocaleString()}</div>
              {(data.unassignedCostUsd > 0 || data.unassignedRequestCount > 0) && (
                // ⚠️ D-424: 合計の合わないグラフを出さないため「所属なし」を正直に併記する。
                //    主所属ノードを設定すれば以降は自然に解消するので、隠さず気づかせる形にする。
                <div className="text-xs text-text-sub mt-1">
                  {/* ⚠️ 「主所属が未設定の人」(下のセクション) と混同されないよう、
                      こちらは**ログに拠点が記録されていない利用**であることを明示する */}
                  うち拠点が記録されていない利用 {data.unassignedRequestCount}回 / ${data.unassignedCostUsd.toFixed(2)}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="border border-surface-line rounded-lg bg-surface-card p-4">
              <h3 className="text-h3 mb-3">機能別内訳</h3>
              {data.byFeature.length === 0 && <p className="text-sm text-text-sub">データがありません</p>}
              <div className="space-y-2">
                {data.byFeature.map((f) => {
                  const Icon = FEATURE_ICONS[f.feature] ?? MessageSquare;
                  const pct = data.totalCostUsd > 0 ? (f.cost_usd / data.totalCostUsd) * 100 : 0;
                  return (
                    <div key={f.feature} className="text-sm">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="flex items-center gap-1.5"><Icon size={14} className="text-text-sub" />{FEATURE_LABELS[f.feature] ?? f.feature}</span>
                        <span className="tabular-nums">${f.cost_usd.toFixed(2)}</span>
                      </div>
                      <div className="w-full h-1.5 rounded-full bg-surface-sunken overflow-hidden">
                        <div className="h-full bg-chart-2" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="border border-surface-line rounded-lg bg-surface-card p-4">
              <h3 className="text-h3 mb-3">モデル別内訳</h3>
              {data.byModel.length === 0 && <p className="text-sm text-text-sub">データがありません</p>}
              <div className="space-y-2">
                {data.byModel.map((m) => {
                  const pct = data.totalCostUsd > 0 ? (m.cost_usd / data.totalCostUsd) * 100 : 0;
                  return (
                    <div key={m.model ?? 'unknown'} className="text-sm">
                      <div className="flex items-center justify-between mb-0.5">
                        <span>{m.model ?? '不明'}</span>
                        <span className="tabular-nums">${m.cost_usd.toFixed(2)}</span>
                      </div>
                      <div className="w-full h-1.5 rounded-full bg-surface-sunken overflow-hidden">
                        <div className="h-full bg-chart-3" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="border border-surface-line rounded-lg bg-surface-card p-4">
            <h3 className="text-h3 mb-3">日別推移</h3>
            {data.byDay.length === 0 ? (
              <p className="text-sm text-text-sub">当月のAI利用実績はまだありません</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.byDay} margin={{ top: 10, right: 12, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v: string) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `$${v}`} />
                  <Tooltip formatter={(v: number) => `$${Number(v).toFixed(2)}`} />
                  <Line type="monotone" dataKey="cost_usd" stroke="#FE6A03" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="border border-surface-line rounded-lg bg-surface-card p-4 overflow-x-auto">
            <h3 className="text-h3 mb-3">ユーザー別 (上位20件)</h3>
            {data.topUsers.length === 0 ? (
              <p className="text-sm text-text-sub">データがありません</p>
            ) : (
              <table className="w-full text-sm min-w-[480px]">
                <thead>
                  <tr className="border-b border-surface-line text-left text-text-sub">
                    <th scope="col" className="py-1.5 font-medium">氏名</th>
                    <th scope="col" className="py-1.5 font-medium text-right">消費USD</th>
                    <th scope="col" className="py-1.5 font-medium text-right">トークン</th>
                    <th scope="col" className="py-1.5 font-medium text-right">リクエスト数</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topUsers.map((u) => (
                    <tr key={u.user_id} className="border-b border-surface-line last:border-0">
                      <td className="py-1.5">{u.display_name}</td>
                      <td className="py-1.5 text-right tabular-nums">${u.cost_usd.toFixed(2)}</td>
                      <td className="py-1.5 text-right tabular-nums">{u.tokens.toLocaleString()}</td>
                      <td className="py-1.5 text-right tabular-nums">{u.request_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
